"use client";

import { createSavingGoal } from "@/app/actions/goal";
import { SavingGoal } from "@prisma/client";
import { useState } from "react";

interface SavingGoalsProps {
    goals: SavingGoal[];
    savedAmount: number;
}

export function SavingGoals({ goals, savedAmount }: SavingGoalsProps) {
    const [isOpen, setIsOpen] = useState(false);

    const handleSubmit = async (formData: FormData) => {
        await createSavingGoal(formData);
        setIsOpen(false); // Close on submit
    };

    return (
        <div className="w-full bg-[#0f172a]/60 backdrop-blur-md rounded-2xl p-6 border border-white/5 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-[#3b82f6]/10 rounded-full blur-3xl -z-10"></div>

            <div className="flex justify-between items-center mb-6">
                <h3 className="text-sm font-bold uppercase tracking-wider text-[#94a3b8] flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-[#3b82f6]"></span> เป้าหมายการออม
                </h3>
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className="text-xs font-bold px-3 py-1.5 rounded-lg bg-[#1e293b] hover:bg-[#334155] text-[#60a5fa] hover:text-[#93c5fd] transition-colors border border-white/5"
                >
                    {isOpen ? "ปิด" : "+ เพิ่มเป้าหมาย"}
                </button>
            </div>

            {isOpen && (
                <form action={handleSubmit} className="mb-6 bg-[#1e293b]/80 p-4 rounded-xl border border-white/10 animate-in slide-in-from-top-2 fade-in">
                    <input name="name" placeholder="เป้าหมาย (เช่น รองเท้าใหม่)" className="w-full bg-[#0f172a] border border-[#334155] rounded-lg p-3 mb-3 text-sm text-white focus:ring-1 focus:ring-[#3b82f6] outline-none" />
                    <input name="amount" type="number" placeholder="ราคา" className="w-full bg-[#0f172a] border border-[#334155] rounded-lg p-3 mb-3 text-sm text-white focus:ring-1 focus:ring-[#3b82f6] outline-none" />
                    <button type="submit" className="w-full bg-[#2563eb] hover:bg-[#3b82f6] text-white text-sm py-3 rounded-lg font-bold shadow-lg shadow-[#3b82f6]/20">เพิ่ม</button>
                </form>
            )}

            <div className="space-y-4">
                {goals.length === 0 && (
                    <div className="text-center py-8 opacity-50">
                        <div className="text-4xl mb-2">🎯</div>
                        <p className="text-xs text-[#94a3b8]">ยังไม่มีเป้าหมายที่ตั้งไว้</p>
                    </div>
                )}

                {goals.map(goal => {
                    const progress = Math.min(100, (savedAmount / goal.targetAmount) * 100);
                    const isCompleted = progress >= 100;

                    return (
                        <div key={goal.id} className="relative group">
                            <div className="flex justify-between text-sm mb-2 items-end">
                                <span className={isCompleted ? "text-[#34d399] font-bold" : "text-[#cbd5e1] font-medium"}>{goal.name}</span>
                                <span className={isCompleted ? "text-[#34d399] font-bold" : "text-[#64748b]"}>
                                    {goal.targetAmount.toLocaleString()}
                                </span>
                            </div>
                            <div className="h-2 bg-[#1e293b] rounded-full overflow-hidden">
                                <div
                                    className={`h-full ${isCompleted ? 'bg-[#10b981] shadow-[0_0_10px_rgba(16,185,129,0.5)]' : 'bg-[#3b82f6]'} transition-all duration-1000 ease-out`}
                                    style={{ width: `${progress}%` }}
                                ></div>
                            </div>
                            {isCompleted && (
                                <div className="absolute -right-2 -top-2 text-[#10b981] text-lg animate-bounce">
                                    ★
                                </div>
                            )}
                        </div>
                    )
                })}
            </div>
        </div>
    );
}
